/**
 * DRESS TO - HIGH-PERFORMANCE BLOG TEMPLATE
 * JavaScript for Enhanced User Experience
 */

(function() {
  'use strict';

  // CONFIGURATION
  const CONFIG = {
    animationDelay: 50,
    searchDebounce: 300,
    scrollOffset: 100,
    lazyLoadMargin: '50px'
  };

  // STATE MANAGEMENT
  const state = {
    currentTag: 'all',
    searchQuery: '',
    isSearching: false,
    articles: []
  };

  // DOM ELEMENTS
  const elements = {
    searchInput: null,
    tagButtons: null,
    articlesGrid: null,
    articleCards: null,
    newsletterForm: null
  };

  // INITIALIZATION
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupApp);
    } else {
      setupApp();
    }
  }

  function setupApp() {
    cacheElements();
    initSearch();
    initTagFilter();
    initAnimations();
    initLazyLoading();
    initNewsletterForm();
    initAccessibility();
    initPerformanceOptimizations();
    console.log('✨ Dress To Blog initialized successfully');
  }

  function cacheElements() {
    elements.searchInput = document.getElementById('blog-search');
    elements.tagButtons = document.querySelectorAll('.tag-btn');
    elements.articlesGrid = document.getElementById('articles-grid');
    elements.articleCards = document.querySelectorAll('.article-card');
    elements.newsletterForm = document.getElementById('newsletter-form');
    
    if (elements.articleCards) {
      state.articles = Array.from(elements.articleCards).map(card => ({
        element: card,
        tags: (card.dataset.tags || '').toLowerCase().split(','),
        title: card.querySelector('.article-title')?.textContent.toLowerCase() || '',
        excerpt: card.querySelector('.article-excerpt')?.textContent.toLowerCase() || '',
        date: card.dataset.date || ''
      }));
    }
  }

  // SEARCH FUNCTIONALITY
  function initSearch() {
    if (!elements.searchInput) return;

    let debounceTimer;
    
    elements.searchInput.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      
      debounceTimer = setTimeout(() => {
        state.searchQuery = e.target.value.toLowerCase().trim();
        filterArticles();
      }, CONFIG.searchDebounce);
    });

    elements.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        elements.searchInput.value = '';
        state.searchQuery = '';
        filterArticles();
      }
    });
  }

  // TAG FILTER
  function initTagFilter() {
    if (!elements.tagButtons) return;

    elements.tagButtons.forEach(button => {
      button.addEventListener('click', () => {
        elements.tagButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');
        
        state.currentTag = button.dataset.tag;
        filterArticles();
        
        announceToScreenReader(`Filtered by ${button.textContent}`);
      });
    });
  }

  // ARTICLE FILTERING
  function filterArticles() {
    if (!state.articles.length) return;

    let visibleCount = 0;
    
    state.articles.forEach(article => {
      const matchesSearch = !state.searchQuery || 
        article.title.includes(state.searchQuery) || 
        article.excerpt.includes(state.searchQuery);
      
      const matchesTag = state.currentTag === 'all' || 
        article.tags.includes(state.currentTag);
      
      const shouldShow = matchesSearch && matchesTag;
      
      if (shouldShow) {
        article.element.style.display = '';
        article.element.classList.remove('hidden');
        visibleCount++;
      } else {
        article.element.style.display = 'none';
        article.element.classList.add('hidden');
      }
    });

    showNoResultsMessage(visibleCount === 0);
    reanimateVisibleCards();
    announceToScreenReader(`${visibleCount} article${visibleCount !== 1 ? 's' : ''} found`);
  }

  function showNoResultsMessage(show) {
    let noResultsMsg = document.getElementById('no-results-message');
    
    if (show && !noResultsMsg) {
      noResultsMsg = document.createElement('div');
      noResultsMsg.id = 'no-results-message';
      noResultsMsg.className = 'no-results';
      noResultsMsg.innerHTML = `
        <div class="no-results-content">
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
            <circle cx="35" cy="35" r="20" stroke="currentColor" stroke-width="3"/>
            <path d="M50 50L65 65" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <h3>No articles found</h3>
          <p>Try adjusting your search or filter to find what you're looking for.</p>
          <button class="reset-filters-btn" onclick="resetFilters()">Clear Filters</button>
        </div>
      `;
      
      if (!document.getElementById('no-results-styles')) {
        const style = document.createElement('style');
        style.id = 'no-results-styles';
        style.textContent = `
          .no-results {
            grid-column: 1 / -1;
            text-align: center;
            padding: 4rem 2rem;
          }
          .no-results svg {
            margin: 0 auto 1.5rem;
            color: #999;
          }
          .no-results h3 {
            font-size: 1.5rem;
            margin-bottom: 0.5rem;
          }
          .no-results p {
            color: #666;
            margin-bottom: 1.5rem;
          }
          .reset-filters-btn {
            padding: 0.75rem 1.5rem;
            background: #8B6F47;
            color: white;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
          }
          .reset-filters-btn:hover {
            background: #6D5639;
          }
        `;
        document.head.appendChild(style);
      }
      
      elements.articlesGrid?.appendChild(noResultsMsg);
    } else if (!show && noResultsMsg) {
      noResultsMsg.remove();
    }
  }

  window.resetFilters = function() {
    if (elements.searchInput) {
      elements.searchInput.value = '';
    }
    state.searchQuery = '';
    state.currentTag = 'all';
    
    elements.tagButtons?.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tag === 'all');
    });
    
    filterArticles();
  };

  // ANIMATIONS
  function initAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const animationObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('aos-animate');
          animationObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    document.querySelectorAll('[data-aos]').forEach(el => {
      animationObserver.observe(el);
    });
  }

  function reanimateVisibleCards() {
    const visibleCards = Array.from(elements.articleCards || [])
      .filter(card => !card.classList.contains('hidden'));
    
    visibleCards.forEach((card, index) => {
      card.style.animationDelay = `${index * CONFIG.animationDelay}ms`;
      card.classList.remove('aos-animate');
      void card.offsetWidth;
      card.classList.add('aos-animate');
    });
  }

  // LAZY LOADING
  function initLazyLoading() {
    if ('loading' in HTMLImageElement.prototype) {
      document.querySelectorAll('img[loading="lazy"]').forEach(img => {
        img.src = img.src;
      });
    } else {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src || img.src;
            img.classList.remove('lazy');
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: CONFIG.lazyLoadMargin
      });

      document.querySelectorAll('img[loading="lazy"]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  // NEWSLETTER FORM
  function initNewsletterForm() {
    if (!elements.newsletterForm) return;

    elements.newsletterForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const formData = new FormData(elements.newsletterForm);
      const submitButton = elements.newsletterForm.querySelector('button[type="submit"]');
      const originalText = submitButton.innerHTML;
      
      submitButton.disabled = true;
      submitButton.innerHTML = 'Subscribing...';
      
      try {
        const response = await fetch(elements.newsletterForm.action, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          showNotification('Success! Check your email to confirm subscription.', 'success');
          elements.newsletterForm.reset();
        } else {
          throw new Error('Subscription failed');
        }
      } catch (error) {
        showNotification('Something went wrong. Please try again.', 'error');
      } finally {
        submitButton.disabled = false;
        submitButton.innerHTML = originalText;
      }
    });
  }

  // NOTIFICATIONS
  function showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    notification.setAttribute('role', 'alert');
    
    if (!document.getElementById('notification-styles')) {
      const style = document.createElement('style');
      style.id = 'notification-styles';
      style.textContent = `
        .notification {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          padding: 1rem 1.5rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 20px 25px rgba(0,0,0,0.12);
          z-index: 9999;
          animation: slideInUp 0.3s ease-out;
          max-width: 400px;
        }
        .notification-success {
          border-left: 4px solid #10b981;
        }
        .notification-error {
          border-left: 4px solid #ef4444;
        }
        @keyframes slideInUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @media (max-width: 768px) {
          .notification {
            left: 1rem;
            right: 1rem;
            bottom: 1rem;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideInUp 0.3s ease-out reverse';
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  // ACCESSIBILITY
  function initAccessibility() {
    if (!document.getElementById('skip-link')) {
      const skipLink = document.createElement('a');
      skipLink.id = 'skip-link';
      skipLink.href = '#main-content';
      skipLink.textContent = 'Skip to main content';
      skipLink.style.cssText = `
        position: absolute;
        left: -9999px;
        z-index: 999;
        padding: 1em;
        background: #8B6F47;
        color: white;
      `;
      skipLink.addEventListener('focus', () => skipLink.style.left = '0');
      skipLink.addEventListener('blur', () => skipLink.style.left = '-9999px');
      document.body.insertBefore(skipLink, document.body.firstChild);
    }

    if (elements.articlesGrid && !document.getElementById('main-content')) {
      elements.articlesGrid.id = 'main-content';
      elements.articlesGrid.setAttribute('tabindex', '-1');
    }
  }

  function announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    announcement.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0,0,0,0);
      white-space: nowrap;
      border: 0;
    `;
    document.body.appendChild(announcement);
    setTimeout(() => announcement.remove(), 1000);
  }

  // PERFORMANCE OPTIMIZATIONS
  function initPerformanceOptimizations() {
    if ('IntersectionObserver' in window) {
      const linkObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const link = entry.target;
            link.addEventListener('mouseenter', prefetchLink, { once: true });
          }
        });
      });

      document.querySelectorAll('.article-link').forEach(link => {
        linkObserver.observe(link);
      });
    }
  }

  function prefetchLink(e) {
    const link = e.target.closest('a');
    if (!link || !link.href) return;
    
    const prefetchLink = document.createElement('link');
    prefetchLink.rel = 'prefetch';
    prefetchLink.href = link.href;
    document.head.appendChild(prefetchLink);
  }

  // SMOOTH SCROLL
  document.addEventListener('click', (e) => {
    const anchor = e.target.closest('a[href^="#"]');
    if (!anchor) return;
    
    const targetId = anchor.getAttribute('href').slice(1);
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      e.preventDefault();
      const offsetTop = targetElement.offsetTop - CONFIG.scrollOffset;
      
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      
      targetElement.focus({ preventScroll: true });
    }
  });

  // SOCIAL SHARING
  window.shareArticle = function(title, url) {
    if (navigator.share) {
      navigator.share({
        title: title,
        url: url
      }).catch(() => {
        copyToClipboard(url);
      });
    } else {
      copyToClipboard(url);
    }
  };

  function copyToClipboard(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    showNotification('Link copied to clipboard!', 'success');
  }

  // START THE APP
  init();

  // EXPOSE PUBLIC API
  window.DressToBlog = {
    filterByTag: (tag) => {
      state.currentTag = tag;
      filterArticles();
    },
    search: (query) => {
      state.searchQuery = query.toLowerCase();
      if (elements.searchInput) {
        elements.searchInput.value = query;
      }
      filterArticles();
    },
    resetFilters: window.resetFilters
  };

})();